import {
  QueryClient,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';

import { api } from 'api';

import { CountResponse, NotificationBase, NotificationList, NotificationSection } from './requests';

type ListProps = {
  unread?: boolean;
  section: NotificationSection;
  offset?: number;
  pageSize?: number;
  newApplication?: boolean;
  assignedToMe?: boolean;
  mentions?: boolean;
  searchValue?: string;
  setCount: (count: number) => void;
};

export const useNotificationList = (props: ListProps) => {
  return useQuery({
    queryKey: ['notifications', 'notifications-list', props],
    queryFn: () => api.notifications.list(props),
    onSuccess: (data) => {
      props.setCount(data.count);
    },
  });
};

export const useNotificationCounts = () => {
  return useQuery({
    queryKey: ['notifications', 'counts'],
    queryFn: () => api.notifications.counts(),
  });
};

const updateIsReadQueryData = (
  queryClient: QueryClient,
  isRead: NotificationBase['isRead'],
  id: NotificationBase['id']
) => {
  queryClient.setQueryData<CountResponse | undefined>(['notifications', 'counts'], (old) => {
    return old ? { ...old, unread: old.unread + (isRead ? -1 : 1) } : undefined;
  });
  queryClient.setQueriesData<NotificationList | undefined>(
    ['notifications', 'notifications-list'],
    (old) => {
      if (old?.results) {
        old.results = old.results.map((notification) => {
          if (notification.id === id) {
            notification.isRead = isRead;
          }
          return notification;
        });
      }
      return old;
    }
  );
};

export const useMarkAsRead = (id: NotificationBase['id']) => {
  const queryClient = useQueryClient();
  return useMutation(() => api.notifications.markAsRead(id), {
    onError: () => {
      queryClient.invalidateQueries(['notifications']);
    },
    onMutate: () => {
      updateIsReadQueryData(queryClient, true, id);
    },
  });
};

export const useMarkAsUnread = (id: NotificationBase['id']) => {
  const queryClient = useQueryClient();
  return useMutation(() => api.notifications.markAsUnread(id), {
    onError: () => {
      queryClient.invalidateQueries(['notifications']);
    },
    onMutate: () => {
      updateIsReadQueryData(queryClient, false, id);
    },
  });
};

export const useMarkAllAsRead = (
  latestLoadedNotificationId: NotificationBase['id'],
  options: UseMutationOptions<AxiosResponse, AxiosError>
) => {
  const queryClient = useQueryClient();
  return useMutation(() => api.notifications.markAllAsRead(latestLoadedNotificationId), {
    onSuccess: (data, variables, context) => {
      options.onSuccess && options.onSuccess(data, variables, context);
    },
    onError: (error: AxiosError, variables, context) => {
      queryClient.invalidateQueries(['notifications']);
      options.onError && options.onError(error, variables, context);
    },
    onMutate: () => {
      queryClient.setQueryData<CountResponse | undefined>(['notifications', 'counts'], (old) => {
        return old ? { ...old, unread: 0 } : undefined;
      });
      queryClient.setQueriesData<NotificationList | undefined>(
        ['notifications', 'notifications-list'],
        (old) => {
          if (old?.results) {
            old.results = old.results.map((notification) => {
              notification.isRead = true;
              return notification;
            });
          }
          return old;
        }
      );
    },
  });
};

const updateArchiveQueryData = (queryClient: QueryClient, id: NotificationBase['id']) => {
  queryClient.setQueriesData<NotificationList | undefined>(
    ['notifications', 'notifications-list'],
    (old) => {
      if (old?.results) {
        old.results = old.results.filter((notification) => notification.id !== id);
      }
      return old;
    }
  );
};

export const useArchive = (id: NotificationBase['id']) => {
  const queryClient = useQueryClient();
  return useMutation(() => api.notifications.archive(id), {
    onError: () => {
      queryClient.invalidateQueries(['notifications']);
    },
    onMutate: () => {
      queryClient.setQueryData<CountResponse | undefined>(['notifications', 'counts'], (old) => {
        return old ? { ...old, total: old.total - 1 } : undefined;
      });
      updateArchiveQueryData(queryClient, id);
    },
  });
};

export const useUnArchive = (id: NotificationBase['id']) => {
  const queryClient = useQueryClient();
  return useMutation(() => api.notifications.unArchive(id), {
    onError: () => {
      queryClient.invalidateQueries(['notifications']);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications', 'counts']);
    },
    onMutate: () => {
      updateArchiveQueryData(queryClient, id);
    },
  });
};

export const useArchiveAll = (
  latestLoadedNotificationId: NotificationBase['id'],
  options: UseMutationOptions<AxiosResponse, AxiosError>
) => {
  const queryClient = useQueryClient();
  return useMutation(() => api.notifications.archiveAll(latestLoadedNotificationId), {
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(['notifications']);
      options.onSuccess && options.onSuccess(data, variables, context);
    },
    onError: (error: AxiosError, variables, context) => {
      options.onError && options.onError(error, variables, context);
    },
  });
};
